import useArrowScroll from '../../../../../hooks/useArrowScroll';
import { INormalizedKnockoutGroup, IPlayoffRoundGroup } from '../../../../../types';
import {
  SMPArrowLeftIcon,
  SMPArrowRightIcon,
  SMPFlex,
  SMPKnockoutDesktopContainer,
  SMPKnockoutGameContainer,
  SMPKnockoutHeaderContainer,
  SMPKnockoutSmallFinal,
  SMPText,
} from '../../../../index';

import { KnockoutHead } from './KnockoutHead';
import { KnockoutGame } from './KnockoutGame';
import { SMALL_FINAL } from '../../../../../constants/playoff';
import { isKnouckoutGameDoubles } from '@sportal365/smp-widget-utils';
import { renderKnockoutGameType } from './KnockoutGameType';
import { useState } from 'react';

type IKnockoutDesktopProps = {
  knockoutGroupNames?: string[];
  scrollWidth?: number;
  smallFinalName?: string;
  hasSmallFinal?: boolean;
  handleEventClick?: (id: string | number) => void;
  sport?: string;
  type?: string;
  isNational?: boolean;
} & (
  | { isPlayoff: false; knockoutGroupGames: (INormalizedKnockoutGroup[] | undefined)[] }
  | { isPlayoff: true; knockoutGroupGames: IPlayoffRoundGroup[][] }
);

const arrowCss = {
  width: '10px',
  position: 'absolute',
  zIndex: 20,
  top: '34px',
  'transition-timing-function': 'ease-out',
  '&:hover': {
    color: 'orange',
    cursor: 'pointer',
    transition: 'color 0.3s',
  },
  '&:active': {
    height: '14px',
    transition: 'height 0.3s',
    'transition-timing-function': 'ease-out',
  },
};

const nextArrowCss = {
  right: '25px',
};

const prevArrowCss = {
  left: '25px',
};

export const KnockoutDesktop = ({
  knockoutGroupNames,
  knockoutGroupGames,
  smallFinalName = SMALL_FINAL,
  hasSmallFinal,
  handleEventClick,
  isPlayoff,
  sport,
  type,
  isNational,
}: IKnockoutDesktopProps): JSX.Element => {
  const { wrapperRef, showPrevArrow, showNextArrow, onScroll, handlePrevToShow, handleNextToShow } = useArrowScroll(320);
  const [hasOpenMenu, setHasOpenMenu] = useState(false);
  const [enableScroll, setEnableScroll] = useState(false);
  const isPlayoffGroup = (group: INormalizedKnockoutGroup | IPlayoffRoundGroup): group is IPlayoffRoundGroup => {
    return isPlayoff;
  };

  const smallFinalWithTwoKnockoutGroups = hasSmallFinal && knockoutGroupGames.length === 2 && !isPlayoff;

  return (
    <SMPKnockoutDesktopContainer onScroll={onScroll} ref={wrapperRef} style={hasOpenMenu && enableScroll ? { overflow: 'unset' } : {}}>
      <>
        {showPrevArrow && (
          <SMPArrowLeftIcon
            onClick={handlePrevToShow}
            css={{
              ...arrowCss,
              ...prevArrowCss,
            }}
          />
        )}
        <SMPKnockoutHeaderContainer
          css={{
            display: 'flex',
            left: 0,
            position: 'sticky',
            width: '100%',
          }}
        />
        <div
          style={{
            height: '48px',
            left: 0,
            marginTop: '-40px',
            width: '100%',
            zIndex: 3,
            display: 'flex',
            position: 'sticky',
            top: 0,
          }}
        />

        <SMPFlex css={{ borderRadius: '$3', flexDirection: 'column' }}>
          <SMPFlex
            className='knockout-rounds-container'
            css={{
              marginTop: '-48px',
              zIndex: 3,
              gridAutoFlow: 'column',
              justifyContent: 'initial',
              gridGap: '50px',
              display: knockoutGroupGames?.length < 3 ? 'inherit' : 'grid',
              width: 'calc(100% - 20px)',
            }}
          >
            {knockoutGroupNames?.map((name, index) => {
              const increaseRowGap = smallFinalWithTwoKnockoutGroups && knockoutGroupNames.length - 1 === index;
              return (
                <div
                  key={name}
                  style={{
                    display: 'grid',
                    gridTemplateRows: '28px',
                    rowGap: increaseRowGap ? '130px' : '12px',
                  }}
                >
                  <KnockoutHead name={name} />

                  <div
                    style={
                      knockoutGroupNames.length - 1 === index && index > 0 && hasSmallFinal
                        ? {
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            marginTop: '24px',
                          }
                        : { marginTop: '24px', display: 'grid', zIndex: 1 }
                    }
                  >
                    {knockoutGroupGames?.[index]?.map((group, idx) => {
                      const removeConnectingLines = knockoutGroupGames.length - 1 === index;
                      return (
                        <SMPKnockoutGameContainer
                          smallFinalWithTwoKnockoutGroups={smallFinalWithTwoKnockoutGroups}
                          smallFinal={hasSmallFinal === true && knockoutGroupNames.length - 2 === index}
                          removeConnectingLines={removeConnectingLines}
                          even={idx % 2 === 0}
                          odd={removeConnectingLines ? false : Math.abs(idx % 2) == 1}
                          firstGroup={index === 0}
                          doublesVariant={isPlayoffGroup(group) && isKnouckoutGameDoubles(type)}
                          variant={'group'}
                          key={group.id}
                        >
                          {knockoutGroupNames.length - 1 === index && index > 0 && hasSmallFinal && Math.abs(idx % 2) == 1 ? (
                            <SMPKnockoutSmallFinal>
                              <SMPText size='3' variant='contrast' css={{ textAlign: 'center' }}>
                                {smallFinalName}
                              </SMPText>
                            </SMPKnockoutSmallFinal>
                          ) : null}
                          {isPlayoffGroup(group) ? (
                            renderKnockoutGameType(
                              group,
                              handleEventClick,
                              sport,
                              type,
                              isNational,
                              name,
                              setHasOpenMenu,
                              false,
                              wrapperRef?.current?.offsetHeight,
                              setEnableScroll,
                            )
                          ) : (
                            <KnockoutGame handleEventClick={handleEventClick} group={group} isNational={isNational} />
                          )}
                        </SMPKnockoutGameContainer>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </SMPFlex>
        </SMPFlex>

        {showNextArrow && (
          <SMPArrowRightIcon
            onClick={handleNextToShow}
            css={{
              ...arrowCss,
              ...nextArrowCss,
            }}
          />
        )}
      </>
    </SMPKnockoutDesktopContainer>
  );
};
